import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Space, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { getFinsystemNumberFormat } from 'src/libs/utils'
import { useCommissionList } from 'src/pages/finSystem/balanceSummary/reports/reportCommissions/hook'
import {
  Commission,
  CommissionFilter,
  SortObject,
} from 'src/pages/finSystem/balanceSummary/reports/reportCommissions/types'
import { CryptoIcon } from 'src/pages/finSystem/wallets/walletsTable/CryptoIcon'
import styles from 'src/pages/finSystem/balanceSummary/reports/styles.module.css'

type Props = {
  sort: SortObject
  setSort: Dispatch<SetStateAction<SortObject>>
  setFilters: (values: Partial<CommissionFilter>) => void
}

export const CommissionListTable = ({ sort, setSort, setFilters }: Props) => {
  const [offsetUSD, setOffsetUsd] = useState(0)
  const [offsetPercent, setOffsetPercent] = useState(0)
  const { commissionList, loading } = useCommissionList()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const usdRef = useRef<HTMLDivElement>(null)
  const percentRef = useRef<HTMLDivElement>(null)

  const { Text } = Typography

  const getSortOrder = (field: string) => {
    if (sort?.field === field) return sort.direction
    return null
  }

  const columns: ColumnsType<Commission> = [
    Table.EXPAND_COLUMN,
    {
      title: 'Wallet ID',
      dataIndex: 'walletId',
      width: '20%',
    },
    {
      title: 'Wallet',
      dataIndex: 'walletName',
      width: '20%',
      render: (name) => (
        <Space>
          <CryptoIcon name={name} size={30} /> {name}
        </Space>
      ),
    },
    {
      title: <div ref={usdRef}>Сумма в USD за период</div>,
      dataIndex: 'commissionAmountUsd',
      width: '20%',
      sorter: true,
      sortOrder: getSortOrder('commissionAmountUsd'),
      render: (it) => (
        <>
          {getFinsystemNumberFormat(it, 2)}{' '}
          <Text type="secondary" style={{ fontSize: 16 }}>
            USD
          </Text>
        </>
      ),
    },
    {
      title: 'В местной валюте',
      width: '20%',
      render: () => <></>,
    },
    {
      title: <div ref={percentRef}>% Потерь</div>,
      dataIndex: 'lossPercentage',
      width: '20%',
      sorter: true,
      sortOrder: getSortOrder('lossPercentage'),
      render: (percent) => (
        <>
          {percent}{' '}
          <Text type="secondary" style={{ fontSize: 16 }}>
            %
          </Text>{' '}
        </>
      ),
    },
  ]
  const { totalCommissionUsd = 0, totalLossPercentage = 0 } =
    commissionList?.totals ?? {}

  useEffect(() => {
    const updateOffset = () => {
      if (!usdRef.current || !percentRef.current || !wrapperRef.current) return

      const usdRect = usdRef.current.getBoundingClientRect()
      const percentRect = percentRef.current.getBoundingClientRect()
      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      setOffsetUsd(usdRect.left - wrapperRect.left)
      setOffsetPercent(percentRect.left - wrapperRect.left)
    }

    updateOffset()

    const resizeObserver = new ResizeObserver(updateOffset)

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [commissionList])

  return (
    <div ref={wrapperRef}>
      <div className={`even-row ${styles.summery}`}>
        <div>Total Amount</div>
        <div style={{ position: 'absolute', left: offsetUSD, bottom: 16 }}>
          {getFinsystemNumberFormat(totalCommissionUsd, 2)} USD
        </div>
        <div style={{ position: 'absolute', left: offsetPercent, bottom: 16 }}>
          {totalLossPercentage} %
        </div>
      </div>

      <Table
        dataSource={commissionList?.items}
        loading={loading}
        columns={columns}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              dataSource={record.currencies}
              pagination={false}
              showHeader={false}
              rowKey={(item) =>
                `${item.currency}_${item.commissionAmountLocal}`
              }
              columns={[
                {
                  dataIndex: 'currency',
                  width: '40%',
                  render: (v) => (
                    <span style={{ marginLeft: '52%' }}>
                      {v?.toUpperCase()}
                    </span>
                  ),
                },
                {
                  dataIndex: 'commissionAmountUsd',
                  render: (it) => (
                    <>
                      {getFinsystemNumberFormat(it, 2)}{' '}
                      <Text type="secondary" style={{ fontSize: 16 }}>
                        USD
                      </Text>
                    </>
                  ),
                  width: '20%',
                },
                {
                  dataIndex: 'commissionAmountLocal',
                  render: (it, record) => (
                    <>
                      {getFinsystemNumberFormat(it, 2)}{' '}
                      <Text type="secondary" style={{ fontSize: 16 }}>
                        {record.currency}
                      </Text>
                    </>
                  ),
                  width: '20%',
                },
                {
                  dataIndex: 'lossPercentage',
                  render: (v) => `${v} %`,
                  width: '20%',
                },
              ]}
              className={styles.row}
            />
          ),
          rowExpandable: (record) => record.currencies?.length > 0,
        }}
        onChange={(p, f, sorter) => {
          const sort = Array.isArray(sorter) ? sorter[0] : sorter
          const { order, field } = sort

          if (!order && !field) {
            setFilters({ sorterField: undefined })
            setSort(undefined)
            return
          }
          const sorterField = order === 'descend' ? `-${field}` : String(field)
          setFilters({ sorterField })
          setSort({ direction: order, field: String(field) })
        }}
        size="large"
        scroll={{ x: true }}
        rowKey={(record) => `${record.walletId}_${record.commissionAmountUsd}`}
        pagination={{
          hideOnSinglePage: true,
          position: ['bottomCenter'],
        }}
        className={styles.row}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
      />
    </div>
  )
}
